import { memo, useContext, useCallback } from 'react';
import { formatCurrency, formatNumber } from '@atc/string-fns';
import { Box, Text, Accordion, AccordionPanel, LoadingSpinner } from 'reaxl';
import _isEmpty from 'lodash/isEmpty';
import { usePersonalPayment, hasIncentiveCashValue, getIncentiveValue } from 'reaxl-wallet';
import BreakdownLineItem from './BreakdownLineItem.js';
import ConfigContext from '../ConfigContext/ConfigContext.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
function getTabTotal(tabName, totalFinancing, adjustedCapCost, totalCashOut) {
  if (tabName === 'lease') {
    return adjustedCapCost;
  }
  if (tabName === 'cash') {
    return totalCashOut;
  }
  return totalFinancing;
}

/**
 * Helper function to determine tab type flags
 * @param {string} tabName - The active payment type tab
 * @returns {Object} Object with isLease, isFinance, and isCash flags
 */
function getTabFlags(tabName) {
  return {
    isLease: tabName === 'lease',
    isFinance: tabName === 'finance',
    isCash: tabName === 'cash'
  };
}

/**
 * Helper function to calculate derived values from pricing and payment data
 * @param {Object} params - The calculation parameters
 * @returns {Object} Object with calculated values
 */
function calculateDerivedValues(_ref) {
  let {
    cashDueFromCustomer,
    diaFee,
    diaTax,
    firstPayment,
    offerPrice,
    listingPrice
  } = _ref;
  return {
    netDownPayment: cashDueFromCustomer - (diaFee + diaTax + firstPayment),
    totalDueAtSigning: cashDueFromCustomer,
    finalPrice: offerPrice ?? listingPrice
  };
}

/**
 * Renders the price information section with listing price, down payment, and trade details
 * @param {Object} params - The parameters for rendering price info
 * @returns {React.ReactElement} The price information section
 */
function PriceInfoSection(_ref2) {
  let {
    offerPrice,
    finalPrice,
    isFinance,
    cashDownLabelDS,
    downPayment,
    disableTrade,
    tradeInValue,
    amountOwed,
    isModal,
    tabName,
    renderTaxesAndFees
  } = _ref2;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(BreakdownLineItem, {
      label: offerPrice ? 'My Offer' : 'Listing Price',
      value: finalPrice
    }), isFinance && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: cashDownLabelDS ?? 'Down Payment',
      value: downPayment,
      priceIndicator: "-"
    }), !disableTrade && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Trade-In Value",
        value: tradeInValue || 0,
        priceIndicator: "-"
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Amount Owed on Trade-In",
        value: amountOwed || 0,
        priceIndicator: "+"
      })]
    }), !isModal && tabName !== 'lease' && renderTaxesAndFees(tabName, 'offerBreakdown'), !isModal && /*#__PURE__*/jsx("hr", {
      className: "margin-vertical-3"
    })]
  });
}

/**
 * Renders financing amount line item for non-modal, non-confirmation contexts
 * @param {Object} params - The parameters for rendering financing amount
 * @returns {React.ReactElement|null} The financing amount line item or null
 */
function FinancingAmountSection(_ref3) {
  let {
    isModal,
    isFinance,
    totalFinancing,
    isBreakdownConfirmation
  } = _ref3;
  if (isModal || !isFinance || !totalFinancing || isBreakdownConfirmation) {
    return null;
  }
  return /*#__PURE__*/jsx(BreakdownLineItem, {
    label: "Amount Financed",
    value: totalFinancing > 0 ? totalFinancing : 0,
    weight: "bold",
    hasDecimals: true
  });
}

/**
 * Renders cash payment line item for non-modal, non-confirmation contexts
 * @param {Object} params - The parameters for rendering cash payment
 * @returns {React.ReactElement|null} The cash payment line item or null
 */
function CashPaymentSection(_ref4) {
  let {
    isCash,
    totalCashOut,
    isBreakdownConfirmation,
    isModal
  } = _ref4;
  if (!isCash || !totalCashOut || isBreakdownConfirmation || isModal) {
    return null;
  }
  return /*#__PURE__*/jsx(BreakdownLineItem, {
    label: "Est. Cash Payment",
    value: totalCashOut > 0 ? totalCashOut : 0,
    weight: "bold",
    hasDecimals: true
  });
}

/**
 * Renders the accordion wrapper for non-modal, non-confirmation contexts
 * @param {Object} params - The parameters for rendering accordion
 * @returns {React.ReactElement} The accordion component
 */
function AccordionWrapper(_ref5) {
  let {
    autoOpen,
    handleOfferBreakdownOpen,
    finalPrice,
    isLease,
    renderLeaseBreakdown,
    renderPriceBreakdown
  } = _ref5;
  return /*#__PURE__*/jsx(Accordion, {
    className: "padding-bottom-4",
    "data-cmp": "offerBreakdown",
    defaultActiveKey: autoOpen ? 'offer-breakdown-panel' : undefined,
    onOpen: handleOfferBreakdownOpen,
    children: /*#__PURE__*/jsxs(AccordionPanel, {
      title: "Offer Breakdown",
      subTitle: formatCurrency(finalPrice),
      eventKey: "offer-breakdown-panel",
      withBorder: true,
      iconPlacement: "before",
      children: [isLease && renderLeaseBreakdown(), !isLease && renderPriceBreakdown()]
    })
  });
}

/**
 * Helper function to determine if estimated payment title should be shown
 * @param {boolean} hasError - Whether there's an error
 * @param {boolean} isModal - Whether in modal context
 * @param {number} total - The total value
 * @returns {boolean} Whether to show payment title
 */
function shouldShowEstimatedPaymentTitle(hasError, isModal, total) {
  return !hasError && isModal && !!total;
}

/**
 * Renders the breakdown content for modal and confirmation contexts
 * @param {Object} params - The parameters for rendering modal content
 * @returns {React.ReactElement} The modal breakdown content
 */
function ModalBreakdownContent(_ref6) {
  let {
    isLease,
    renderLeaseBreakdown,
    renderPriceBreakdown,
    isModal,
    renderTaxesAndFees,
    tabName,
    hasError,
    total,
    estimatedPaymentTitle,
    payment,
    isCash,
    isLoading
  } = _ref6;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [isLease && renderLeaseBreakdown(), !isLease && renderPriceBreakdown(), isModal && renderTaxesAndFees(tabName, 'offerBreakdownModal'), shouldShowEstimatedPaymentTitle(hasError, isModal, total) && estimatedPaymentTitle({
      payment,
      tabName,
      total,
      isCash,
      isLoading
    })]
  });
}

/**
 * Helper function to determine if incentives should be displayed in modal
 * @param {boolean} isModal - Whether in modal context
 * @param {boolean} disableIncentives - Whether incentives are disabled
 * @param {boolean} isFetchingIncentives - Whether incentives are being fetched
 * @param {Array} appliedIncentives - Applied incentives array
 * @returns {boolean} Whether to show incentives
 */
function shouldShowIncentivesInModal(isModal, disableIncentives, isFetchingIncentives, appliedIncentives) {
  return isModal && !disableIncentives && !isFetchingIncentives && appliedIncentives.length > 0;
}

/**
 * Renders an inline loading spinner component for use within breakdown line items
 * @returns {React.ReactElement} A loading spinner with appropriate styling for inline display
 */
function renderInlineLoadingSpinner() {
  return /*#__PURE__*/jsx("div", {
    className: "text-right",
    children: /*#__PURE__*/jsx(LoadingSpinner, {
      className: "display-inline-block margin-0"
    })
  });
}

/**
 * Formats and renders finance terms string including APR rate and loan term
 * @param {Object} params - The parameters object
 * @param {number} params.rate - Annual Percentage Rate (APR) as a percentage (e.g., 3.9 for 3.9%)
 * @param {number} params.term - Loan term duration in months (e.g., 36, 48, 60)
 * @param {boolean} params.showSpecialAPR - Flag to indicate if this is a special promotional APR offer
 * @returns {string} Formatted string showing APR and term information (e.g., "Special 3.9% APR for 36 mo.")
 */
function renderFinanceTerms(_ref7) {
  let {
    rate,
    term,
    showSpecialAPR
  } = _ref7;
  if (showSpecialAPR) {
    return `Special ${rate}% APR for ${term} mo.`;
  }
  return `${rate}% APR for ${term} mo.`;
}

/**
 * Renders the discount information section with MSRP, manufacturer offers, and dealer adjustments
 * @param {Object} params - The parameters object
 * @param {boolean} params.showMsrp - Flag to control MSRP visibility in the breakdown
 * @param {string} params.priceLabel - Custom label for MSRP display (e.g., 'TSRP' for Toyota)
 * @param {number} params.msrp - Manufacturer's Suggested Retail Price
 * @param {number} params.manufacturerOffer - Value of manufacturer's discount or incentive
 * @param {number} params.offersUpgrades - Value of dealer adjustments or upgrades (positive for upgrades, negative for discounts)
 * @param {boolean} params.enableOfferUpgrades - Flag to control visibility of dealer adjustments section
 * @param {boolean} params.disableIncentives - Flag to control visibility of manufacturer incentives section
 * @returns {React.ReactElement|null} The rendered discount information section or null if no discount data is available
 */

function renderDiscountInfo(_ref8) {
  let {
    showMsrp,
    priceLabel,
    msrp,
    manufacturerOffer,
    offersUpgrades,
    enableOfferUpgrades,
    disableIncentives,
    appliedIncentives = [],
    isFetchingIncentives = false,
    hasIncentivesError = false,
    isModal = false
  } = _ref8;
  const hasMsrp = showMsrp && msrp;
  const hasManufacturerOffer = !disableIncentives && !!manufacturerOffer;
  const hasOffersUpgrades = enableOfferUpgrades && !!offersUpgrades;
  const hasAppliedIncentives = !disableIncentives && appliedIncentives.length > 0 && !hasIncentivesError;

  // Determine if we should show manufacturer offer in modal context
  const showManufacturerOffer = hasManufacturerOffer && !isFetchingIncentives && (!isModal || appliedIncentives.length === 0 || !appliedIncentives.some(hasIncentiveCashValue));
  if (!hasMsrp && !hasManufacturerOffer && !hasOffersUpgrades && !hasAppliedIncentives) return null;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [showMsrp && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: priceLabel,
      value: msrp
    }), isModal && !disableIncentives && isFetchingIncentives && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: renderInlineLoadingSpinner(),
      value: renderInlineLoadingSpinner()
    }, "loading"), shouldShowIncentivesInModal(isModal, disableIncentives, isFetchingIncentives, appliedIncentives) && appliedIncentives.map((incentive, index) => /*#__PURE__*/jsx(BreakdownLineItem, {
      label: incentive.programName,
      value: getIncentiveValue(incentive),
      priceIndicator: "-"
    }, `${index}-${incentive.id}`)), showManufacturerOffer && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: "Manufacturer Offer",
      value: manufacturerOffer,
      priceIndicator: "-"
    }), enableOfferUpgrades && !!offersUpgrades && /*#__PURE__*/jsx(BreakdownLineItem, {
      label: offersUpgrades < 0 ? 'Dealer Adjustment' : 'Dealer Discount',
      value: Math.abs(offersUpgrades),
      priceIndicator: offersUpgrades < 0 ? '+' : '-'
    }), /*#__PURE__*/jsx("hr", {
      className: "margin-vertical-3"
    })]
  });
}

/**
 * Renders the section showing amounts due in advance including fees, taxes, and payments
 * @param {Object} params - The parameters object
 * @param {number} params.netDownPayment - Total amount customer needs to bring at signing
 * @param {number} params.diaFee - Sum of all applicable dealer, documentation, and administrative fees
 * @param {number} params.diaTax - Sum of all applicable sales tax, title tax, and other taxes
 * @param {number} params.firstPayment - Amount of first month's payment
 * @param {number} params.totalDueAtSigning - Total amount required at signing after all calculations
 * @param {string} params.isEstimate - Determines if the 'Est." label is shown
 * @returns {React.ReactElement} The rendered section showing itemized amounts due in advance
 */
function renderDueInAdvanceInfo(_ref9) {
  let {
    netDownPayment,
    diaFee,
    diaTax,
    firstPayment,
    totalDueAtSigning,
    isEstimate,
    isDueAtSigningAmountDeduct = false,
    cashDownLabelDS = null,
    isModal = false
  } = _ref9;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("li", {
      className: "display-flex justify-content-between align-items-center margin-top-3 text-subdued",
      children: /*#__PURE__*/jsx(Text, {
        weight: "bold",
        size: 300,
        children: "Due at Signing"
      })
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: isEstimate ? 'Est. Fees' : 'Fees',
      value: diaFee,
      priceIndicator: "+",
      hasDecimals: true
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: isEstimate ? 'Est. Taxes' : 'Taxes',
      value: diaTax,
      priceIndicator: "+",
      hasDecimals: true
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: "First Months Payment",
      value: firstPayment,
      priceIndicator: "+",
      hasDecimals: true
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: !isDueAtSigningAmountDeduct && cashDownLabelDS ? cashDownLabelDS : 'Down Payment',
      value: netDownPayment,
      priceIndicator: "-",
      hasDecimals: true
    }), /*#__PURE__*/jsx(BreakdownLineItem, {
      label: "Total Due at Signing",
      value: totalDueAtSigning,
      weight: "bold",
      hasDecimals: true,
      className: isModal ? 'margin-bottom-3' : ''
    }), !isModal && /*#__PURE__*/jsx("hr", {
      className: "margin-vertical-3"
    })]
  });
}

/**
 * Renders the confirmation section of the breakdown showing finance or lease details
 * @param {Object} params - The parameters object
 * @param {boolean} params.isFinance - Flag indicating if this is a finance deal
 * @param {boolean} params.isLease - Flag indicating if this is a lease deal
 * @param {boolean} params.isCash - Flag indicating if this is a cash deal
 * @param {number} params.totalFinancing - Total amount being financed
 * @param {number} params.adjustedCapCost - Adjusted capitalized cost for lease calculations
 * @param {number} params.totalCashOut - Total cash payment required for cash deals
 * @param {number} params.financePayment - Monthly payment for finance deals
 * @param {number} params.leasePayment - Monthly payment for lease deals
 * @param {string} params.lender - Name of financing institution providing the loan or lease
 * @param {Object} params.paymentsInfo - Object containing payment terms and conditions
 * @param {number} params.paymentsInfo.loanTerm - Duration of the loan in months
 * @param {number} params.paymentsInfo.leaseTerm - Duration of the lease in months
 * @param {boolean} params.paymentsInfo.showSpecialAPR - Whether this is a special APR offer
 * @param {number} params.financeRate - Annual Percentage Rate for financing as a percentage
 * @returns {React.ReactElement|null} The rendered confirmation section or null if no applicable data
 */

function renderBreakdownConfirmation(_ref10) {
  let {
    isFinance,
    isLease,
    isCash,
    totalFinancing,
    adjustedCapCost,
    totalCashOut,
    financePayment,
    leasePayment,
    lender,
    paymentsInfo,
    financeRate
  } = _ref10;
  if (!(isFinance && !!totalFinancing) && !(isLease && !!adjustedCapCost) && !(isCash && !!totalCashOut)) {
    return null;
  }
  return /*#__PURE__*/jsxs(Box, {
    backgroundColor: "brand-primary",
    children: [isFinance && !!totalFinancing && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(BreakdownLineItem, {
        weight: "bold",
        label: "Finance Amount",
        value: totalFinancing > 0 ? totalFinancing : 0,
        hasDecimals: true
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Estimated Monthly Payment",
        value: Math.trunc(financePayment),
        size: 100,
        valueSuffix: "/mo."
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: lender,
        valueSuffix: renderFinanceTerms({
          term: paymentsInfo?.loanTerm,
          rate: financeRate,
          showSpecialAPR: paymentsInfo?.showSpecialAPR
        }),
        size: 100
      })]
    }), isLease && !!adjustedCapCost && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(BreakdownLineItem, {
        weight: "bold",
        label: "Est. Adjusted Cap Cost",
        value: adjustedCapCost > 0 ? adjustedCapCost : 0,
        hasDecimals: true
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Estimated Monthly Payment",
        value: Math.trunc(leasePayment),
        valueSuffix: "/mo.",
        size: 100
      }), /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Lease Terms",
        valueSuffix: `for ${paymentsInfo?.leaseTerm} mo.`,
        size: 100
      })]
    }), isCash && !!totalCashOut && /*#__PURE__*/jsx(BreakdownLineItem, {
      weight: "bold",
      label: "Estimated Cash Payment",
      value: totalCashOut > 0 ? totalCashOut : 0,
      hasDecimals: true
    })]
  });
}

/**
 * Renders a finance amount tile displaying the total amount and monthly payment information
 * @param {Object} props - Component properties
 * @param {number} props.payment - The calculated monthly payment amount
 * @param {string} props.tabName - The active tab name ('finance', 'lease', 'cash')
 * @param {number} props.total - The total amount (finance amount, adjusted cap cost, or cash payment)
 * @param {boolean} props.isLoading - Whether taxes are currently being loaded
 * @param {boolean} props.isCash - Whether this is a cash deal (affects monthly payment display)
 * @returns {React.ReactElement} A tile component showing financial summary information
 */
const estimatedPaymentTitle = _ref11 => {
  let {
    payment,
    tabName,
    total,
    isLoading,
    isCash
  } = _ref11;
  const paymentValue = formatNumber({
    value: Math.floor(payment)
  });
  let totalTitle = 'Finance Amount';
  if (tabName === 'lease') {
    totalTitle = 'Est. Adjusted Cap Cost';
  } else if (tabName === 'cash') {
    totalTitle = 'Est. Cash Payment';
  }
  const renderListItem = function (label, content) {
    let textProps = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    return /*#__PURE__*/jsxs("li", {
      className: "display-flex justify-content-between align-items-center",
      children: [/*#__PURE__*/jsx(Text, {
        size: 300,
        ...textProps,
        children: label
      }), isLoading ? renderInlineLoadingSpinner() : /*#__PURE__*/jsx(Text, {
        className: "display-flex justify-content-between gap-3",
        size: 300,
        ...textProps,
        children: content
      })]
    });
  };
  return /*#__PURE__*/jsx(Box, {
    backgroundColor: "brand-primary",
    className: "margin-vertical-3",
    children: /*#__PURE__*/jsxs("ul", {
      className: "list-unstyled margin-bottom-0",
      children: [renderListItem(totalTitle, total > 0 ? formatCurrency(Math.floor(total)) : 0, {
        weight: 'bold'
      }), !isCash && renderListItem('Est. Monthly Payment', `$${paymentValue}/mo.*`, {
        color: 'neutral-primary'
      })]
    })
  });
};

/**
 * A component that displays a detailed breakdown of a vehicle offer with support for financing,
 * leasing, and cash payment options. It shows a complete cost structure including MSRP (or TSRP for Toyota), discounts,
 * trade-in values, taxes, and fees with accordion-style expandable sections.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {number} [props.adjustedCapCost] - Adjusted capitalized cost for lease calculations
 * @param {number} [props.amountOwed] - Outstanding balance on trade-in vehicle
 * @param {boolean} [props.autoOpen=false] - Whether to expand the breakdown accordion by default
 * @param {number} [props.capitalizedTaxesAndFees] - Amount of capitalized taxes and fees for lease calculations
 * @param {number} [props.cashDueFromCustomer] - Total cash amount the customer needs to bring at signing
 * @param {number} [props.diaFee] - Sum of all applicable dealer, documentation, and administrative fees
 * @param {number} [props.diaTax] - Sum of all applicable sales tax, title tax, and other taxes
 * @param {boolean} [props.disableIncentives=false] - Whether to hide manufacturer incentives section
 * @param {boolean} [props.disableTrade] - Whether to hide trade-in related information
 * @param {number} [props.downPayment] - Initial down payment amount
 * @param {boolean} [props.enableOfferUpgrades=true] - Whether to show dealer adjustments section
 * @param {boolean} [props.enablePriceBreakdown=true] - Whether to show detailed price structure breakdown
 * @param {number} [props.financePayment] - Monthly financing payment amount
 * @param {number} [props.financeRate] - Annual Percentage Rate for financing as a percentage
 * @param {number} [props.firstPayment] - Amount of first month's payment
 * @param {Function} props.getPricingBreakdown - Function to retrieve pricing details by payment type
 * @param {Function} [props.handleOfferBreakdownOpen] - Function to handle opening the offer breakdown accordion
 * @param {boolean|string} [props.isEstimate] - Determines if the 'Est.' label is shown on various fields
 * @param {boolean} [props.isModal=false] - Whether the price breakdown is displayed in a modal context
 * @param {number} [props.leasePayment] - Monthly lease payment amount
 * @param {string} [props.lender] - Name of financing institution providing the loan or lease
 * @param {Object} [props.monthlyPaymentProps] - Additional props for monthly payment calculations
 * @param {Object} [props.paymentsInfo] - Details about payment terms and conditions including loan/lease terms
 * @param {Function} [props.renderTaxesAndFees] - Function to render customized tax and fee information
 * @param {boolean} [props.showMsrp=true] - Whether to display MSRP in the breakdown
 * @param {string} [props.priceLabel='MSRP'] - Custom label for MSRP display (e.g., 'TSRP' for Toyota)
 * @param {string} [props.tabName='finance'] - Active payment type tab ('finance', 'lease', 'cash')
 * @param {number} [props.totalCashOut] - Total cash payment required for cash deals
 * @param {number} [props.totalFinancing] - Total amount being financed
 * @param {number} [props.tradeInValue] - Value of trade-in vehicle
 * @param {string} [props.uiContext='default'] - UI context for rendering ('default', 'confirmation', etc.)
 * @param {boolean} [props.hasError] - Whether there is an error in the breakdown
 * @param {Array} [props.appliedIncentives] - List of applied incentives
 * @param {boolean} [props.isFetchingIncentives] - Whether incentives are being fetched
 * @param {boolean} [props.hasIncentivesError] - Whether there was an error fetching incentives
 * @returns {React.ReactElement|null} The offer breakdown component or null if no pricing data
 */
function OfferBreakdown(_ref12) {
  let {
    adjustedCapCost,
    amountOwed,
    autoOpen = false,
    disableIncentives = false,
    disableTrade,
    downPayment,
    enableOfferUpgrades = true,
    enablePriceBreakdown = true,
    financePayment,
    getPricingBreakdown,
    leasePayment,
    lender,
    paymentsInfo,
    financeRate,
    renderTaxesAndFees,
    showMsrp = true,
    tabName = 'finance',
    totalCashOut,
    totalFinancing,
    tradeInValue,
    uiContext = 'deafult',
    diaFee,
    priceLabel = 'MSRP',
    diaTax,
    firstPayment,
    cashDueFromCustomer,
    isEstimate,
    capitalizedTaxesAndFees,
    isModal = false,
    handleOfferBreakdownOpen = () => {},
    monthlyPaymentProps = {},
    hasError = false,
    appliedIncentives = [],
    isFetchingIncentives = false,
    hasIncentivesError = false
  } = _ref12;
  const {
    isDueAtSigningAmountDeduct = false,
    oemLabels = {}
  } = useContext(ConfigContext) ?? {};
  const {
    cashDownLabelDS
  } = oemLabels;
  const total = getTabTotal(tabName, totalFinancing, adjustedCapCost, totalCashOut);
  const {
    isLease,
    isFinance,
    isCash
  } = getTabFlags(tabName);
  const pricingDetail = getPricingBreakdown(tabName);
  const isBreakdownConfirmation = uiContext === 'confirmation';
  const {
    listingPrice,
    manufacturerOffer,
    msrp,
    offerPrice,
    offersUpgrades
  } = pricingDetail;
  const {
    finalPrice,
    netDownPayment,
    totalDueAtSigning
  } = calculateDerivedValues({
    cashDueFromCustomer,
    diaFee,
    diaTax,
    firstPayment,
    offerPrice,
    listingPrice
  });
  const {
    price,
    paymentsInfo: monthlyPaymentsInfo,
    vehicleCondition,
    personalizedPayment,
    isLoading
  } = monthlyPaymentProps || {};
  const payment = usePersonalPayment({
    price,
    paymentsInfo: monthlyPaymentsInfo,
    vehicleCondition,
    personalizedPayment
  });
  const renderPriceInfo = useCallback(() => /*#__PURE__*/jsx(PriceInfoSection, {
    offerPrice: offerPrice,
    finalPrice: finalPrice ?? listingPrice,
    isFinance: isFinance,
    cashDownLabelDS: cashDownLabelDS,
    downPayment: downPayment,
    disableTrade: disableTrade,
    tradeInValue: tradeInValue,
    amountOwed: amountOwed,
    isModal: isModal,
    tabName: tabName,
    renderTaxesAndFees: renderTaxesAndFees
  }), [offerPrice, finalPrice, isFinance, cashDownLabelDS, downPayment, disableTrade, tradeInValue, amountOwed, isModal, tabName, renderTaxesAndFees, listingPrice]);
  const renderPriceBreakdown = useCallback(() => /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("ul", {
      className: "list-unstyled",
      children: [enablePriceBreakdown && renderDiscountInfo({
        showMsrp,
        priceLabel,
        msrp,
        manufacturerOffer,
        offersUpgrades,
        enableOfferUpgrades,
        disableIncentives,
        appliedIncentives,
        isFetchingIncentives,
        hasIncentivesError,
        isModal
      }), renderPriceInfo(), /*#__PURE__*/jsx(FinancingAmountSection, {
        isModal: isModal,
        isFinance: isFinance,
        totalFinancing: totalFinancing,
        isBreakdownConfirmation: isBreakdownConfirmation
      }), /*#__PURE__*/jsx(CashPaymentSection, {
        isCash: isCash,
        totalCashOut: totalCashOut,
        isBreakdownConfirmation: isBreakdownConfirmation,
        isModal: isModal
      })]
    }), isBreakdownConfirmation && renderBreakdownConfirmation({
      isFinance,
      isLease,
      isCash,
      totalFinancing,
      adjustedCapCost,
      totalCashOut,
      financePayment,
      leasePayment,
      lender,
      paymentsInfo,
      financeRate
    })]
  }), [enablePriceBreakdown, showMsrp, priceLabel, msrp, manufacturerOffer, offersUpgrades, enableOfferUpgrades, disableIncentives, renderPriceInfo, isFinance, totalFinancing, isBreakdownConfirmation, isCash, totalCashOut, isLease, adjustedCapCost, financePayment, leasePayment, lender, paymentsInfo, financeRate, isModal, appliedIncentives, hasIncentivesError, isFetchingIncentives]);
  const renderLeaseBreakdown = useCallback(() => /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("ul", {
      className: "list-unstyled",
      children: [enablePriceBreakdown && renderDiscountInfo({
        showMsrp,
        priceLabel,
        msrp,
        manufacturerOffer,
        offersUpgrades,
        enableOfferUpgrades,
        disableIncentives,
        appliedIncentives,
        isFetchingIncentives,
        hasIncentivesError,
        isModal
      }), renderPriceInfo(), renderDueInAdvanceInfo({
        diaFee,
        diaTax,
        firstPayment,
        totalDueAtSigning,
        isEstimate,
        netDownPayment,
        isDueAtSigningAmountDeduct,
        cashDownLabelDS,
        isModal
      }), !isModal && /*#__PURE__*/jsx(BreakdownLineItem, {
        label: isEstimate ? 'Est. Taxes & Fees' : 'Taxes & Fees',
        value: capitalizedTaxesAndFees,
        infoIconText: "Usually includes sales tax, DMV fees, any lender fees for financing, plus dealer-related fees for doc and prep",
        className: "margin-bottom-3"
      }), !isModal && !!adjustedCapCost && !isBreakdownConfirmation && /*#__PURE__*/jsx(BreakdownLineItem, {
        label: "Est. Adjusted Cap Cost",
        value: adjustedCapCost > 0 ? adjustedCapCost : 0,
        weight: "bold",
        hasDecimals: true
      })]
    }), isBreakdownConfirmation && renderBreakdownConfirmation({
      isLease,
      adjustedCapCost,
      leasePayment,
      paymentsInfo
    })]
  }), [enablePriceBreakdown, showMsrp, priceLabel, msrp, manufacturerOffer, offersUpgrades, enableOfferUpgrades, disableIncentives, renderPriceInfo, diaFee, diaTax, firstPayment, cashDueFromCustomer, adjustedCapCost, isBreakdownConfirmation, isLease, leasePayment, paymentsInfo, isEstimate, totalDueAtSigning, netDownPayment, capitalizedTaxesAndFees, cashDownLabelDS, isDueAtSigningAmountDeduct, isModal, appliedIncentives, hasIncentivesError, isFetchingIncentives]);
  if (!pricingDetail || _isEmpty(pricingDetail)) {
    return null;
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isBreakdownConfirmation && !isModal && /*#__PURE__*/jsx(AccordionWrapper, {
      autoOpen: autoOpen,
      handleOfferBreakdownOpen: handleOfferBreakdownOpen,
      finalPrice: finalPrice,
      isLease: isLease,
      renderLeaseBreakdown: renderLeaseBreakdown,
      renderPriceBreakdown: renderPriceBreakdown
    }), (isBreakdownConfirmation || isModal) && /*#__PURE__*/jsx(ModalBreakdownContent, {
      isLease: isLease,
      renderLeaseBreakdown: renderLeaseBreakdown,
      renderPriceBreakdown: renderPriceBreakdown,
      isModal: isModal,
      renderTaxesAndFees: renderTaxesAndFees,
      tabName: tabName,
      hasError: hasError,
      total: total,
      estimatedPaymentTitle: estimatedPaymentTitle,
      payment: payment,
      isCash: isCash,
      isLoading: isLoading
    })]
  });
}
var OfferBreakdown$1 = /*#__PURE__*/memo(OfferBreakdown);
export { OfferBreakdown$1 as default };
